import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { PortfolioService } from '../../../services/portfolioService';

const ContactForm = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    projectType: searchParams.get('type') === 'web-project' ? 'web-app' : '',
    budget: '',
    timeline: '',
    message: '',
    preferredContact: '',
    urgency: '',
    subject: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const projectTypes = [
    { value: 'web-app', label: 'Web Application Development' },
    { value: 'mobile-app', label: 'Mobile App Development' },
    { value: 'api-backend', label: 'API & Backend Development' },
    { value: 'consulting', label: 'Technical Consulting' },
    { value: 'code-review', label: 'Code Review & Audit' },
    { value: 'mentoring', label: 'Mentoring & Training' },
    { value: 'collaboration', label: 'Open Source Collaboration' },
    { value: 'other', label: 'Other (Please specify)' }
  ];

  const budgetRanges = [
    { value: 'under-5k', label: 'Under $5,000' },
    { value: '5k-15k', label: '$5,000 - $15,000' },
    { value: '15k-50k', label: '$15,000 - $50,000' },
    { value: '50k-plus', label: '$50,000+' },
    { value: 'discuss', label: "Let's discuss" }
  ];

  const timelines = [
    { value: 'asap', label: 'ASAP (Rush project)' },
    { value: '1-month', label: '1 Month' },
    { value: '2-3-months', label: '2-3 Months' },
    { value: '3-6-months', label: '3-6 Months' },
    { value: '6-months-plus', label: '6+ Months' },
    { value: 'flexible', label: 'Flexible timeline' }
  ];

  const contactPreferences = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone Call' },
    { value: 'video', label: 'Video Call' },
    { value: 'linkedin', label: 'LinkedIn Message' }
  ];

  const urgencyLevels = [
    { value: 'low', label: 'Low - General inquiry' },
    { value: 'medium', label: 'Medium - Project discussion' },
    { value: 'high', label: 'High - Time-sensitive' },
    { value: 'urgent', label: 'Urgent - Need immediate response' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Clear submit status when user starts typing
    if (submitStatus?.message) {
      setSubmitStatus({ type: '', message: '' });
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData?.name?.trim()) newErrors.name = 'Name is required';
      if (!formData?.email?.trim()) newErrors.email = 'Email is required';
      if (formData?.email && !/\S+@\S+\.\S+/?.test(formData?.email)) {
        newErrors.email = 'Please enter a valid email';
      }
      if (!formData?.projectType) newErrors.projectType = 'Please select a project type';
    }
    
    if (step === 2) {
      if (!formData?.message?.trim()) newErrors.message = 'Please describe your project';
      if (formData?.message?.length < 20) {
        newErrors.message = 'Please provide more details (minimum 20 characters)';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validateStep(2)) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });
    
    try {
      // Submit contact form using PortfolioService
      const result = await PortfolioService?.submitContactMessage(formData);

      if (result?.error) {
        setSubmitStatus({
          type: 'error',
          message: result?.error
        });
      } else {
        setSubmitStatus({
          type: 'success',
          message: result?.message || 'Thank you! Your message has been sent successfully. I\'ll get back to you within 24 hours.'
        });

        // Reset form on success
        setFormData({
          name: '', email: '', company: '', projectType: '', budget: '',
          timeline: '', message: '', preferredContact: '', urgency: '', subject: ''
        });
        setCurrentStep(1);
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Something went wrong. Please try again or contact me directly via email.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const steps = [
    { number: 1, title: 'Basic Info', description: 'Tell me about yourself' },
    { number: 2, title: 'Project Details', description: 'Describe your project' }
  ];

  return (
    <section id="contact-form" className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
            Start a Conversation
          </h2>
          <p className="text-xl text-slate-600">
            Tell me about your project and let's explore how we can work together
          </p>
        </div>

        {/* Progress Steps */}
        <div className="mb-12">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            {steps?.map((step, index) => (
              <div key={step?.number} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all duration-300 ${
                    currentStep >= step?.number
                      ? 'bg-blue-600 text-white' :'bg-slate-200 text-slate-500'
                  }`}>
                    {currentStep > step?.number ? (
                      <Icon name="Check" size={16} />
                    ) : (
                      step?.number
                    )}
                  </div>
                  <div className="mt-2 text-center">
                    <div className="text-sm font-medium text-slate-900">{step?.title}</div>
                    <div className="text-xs text-slate-500">{step?.description}</div>
                  </div>
                </div>
                {index < steps?.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 transition-all duration-300 ${
                    currentStep > step?.number ? 'bg-blue-600' : 'bg-slate-200'
                  }`}></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Status Message */}
        {submitStatus?.message && (
          <div className={`mb-8 p-4 rounded-lg border ${
            submitStatus?.type === 'success' ?'bg-green-50 border-green-200 text-green-800' :'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-start">
              <Icon 
                name={submitStatus?.type === 'success' ? 'CheckCircle' : 'AlertCircle'} 
                size={20} 
                className={`mr-3 mt-0.5 flex-shrink-0 ${
                  submitStatus?.type === 'success' ? 'text-green-600' : 'text-red-600'
                }`} 
              />
              <div>
                <p className="font-medium">
                  {submitStatus?.type === 'success' ? 'Message Sent!' : 'Error Occurred'}
                </p>
                <p className="mt-1 text-sm">{submitStatus?.message}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <div className="bg-slate-50 rounded-2xl p-8">
          <form onSubmit={handleSubmit}>
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Full Name"
                    type="text"
                    placeholder="Enter your full name"
                    value={formData?.name}
                    onChange={(e) => handleInputChange('name', e?.target?.value)}
                    error={errors?.name}
                    required
                  />
                  <Input
                    label="Email Address"
                    type="email"
                    placeholder="your.email@company.com"
                    value={formData?.email}
                    onChange={(e) => handleInputChange('email', e?.target?.value)}
                    error={errors?.email}
                    required
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Input
                    label="Company/Organization"
                    type="text"
                    placeholder="Your company name (optional)"
                    value={formData?.company}
                    onChange={(e) => handleInputChange('company', e?.target?.value)}
                  />
                  <Input
                    label="Subject"
                    type="text"
                    placeholder="Brief subject line (optional)"
                    value={formData?.subject}
                    onChange={(e) => handleInputChange('subject', e?.target?.value)}
                  />
                </div>

                <Select
                  label="Project Type"
                  placeholder="What type of project are you working on?"
                  options={projectTypes}
                  value={formData?.projectType}
                  onChange={(value) => handleInputChange('projectType', value)}
                  error={errors?.projectType}
                  required
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Select
                    label="Budget Range"
                    placeholder="Select budget range"
                    options={budgetRanges}
                    value={formData?.budget}
                    onChange={(value) => handleInputChange('budget', value)}
                  />
                  <Select
                    label="Timeline"
                    placeholder="When do you need this completed?"
                    options={timelines}
                    value={formData?.timeline}
                    onChange={(value) => handleInputChange('timeline', value)}
                  />
                </div>

                <div className="flex justify-end">
                  <Button
                    type="button"
                    onClick={handleNext}
                    iconName="ArrowRight"
                    iconPosition="right"
                  >
                    Next Step
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Project Details */}
            {currentStep === 2 && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Project Description *
                  </label>
                  <textarea
                    rows={6}
                    placeholder={`Please describe your project in detail. Include:\n• What you're trying to build\n• Key features and functionality\n• Target audience\n• Technical requirements\n• Any specific challenges you're facing`}
                    value={formData?.message}
                    onChange={(e) => handleInputChange('message', e?.target?.value)}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                      errors?.message ? 'border-red-500' : 'border-slate-300'
                    }`}
                  />
                  {errors?.message && (
                    <p className="mt-1 text-sm text-red-600">{errors?.message}</p>
                  )}
                  <p className="mt-2 text-sm text-slate-500">
                    {formData?.message?.length}/500 characters
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Select
                    label="Preferred Contact Method"
                    placeholder="How would you like me to respond?"
                    options={contactPreferences}
                    value={formData?.preferredContact}
                    onChange={(value) => handleInputChange('preferredContact', value)}
                  />
                  <Select
                    label="Urgency Level"
                    placeholder="How urgent is this project?"
                    options={urgencyLevels}
                    value={formData?.urgency}
                    onChange={(value) => handleInputChange('urgency', value)}
                  />
                </div>

                <div className="flex justify-between">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handlePrevious}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Previous
                  </Button>
                  <Button
                    type="submit"
                    loading={isSubmitting}
                    iconName="Send"
                    iconPosition="right"
                    className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>

        {/* Form Benefits */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="Shield" size={24} className="text-blue-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Secure & Private</h3>
            <p className="text-sm text-slate-600">Your information is encrypted and never shared with third parties</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="Clock" size={24} className="text-emerald-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">Quick Response</h3>
            <p className="text-sm text-slate-600">I typically respond within 24 hours, often much sooner</p>
          </div>
          <div className="text-center">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircle" size={24} className="text-purple-600" />
            </div>
            <h3 className="font-semibold text-slate-900 mb-2">No Commitment</h3>
            <p className="text-sm text-slate-600">Free consultation to discuss your project and explore options</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
